import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Download, RefreshCw, Search, X, FileText, RotateCcw,
  Printer, Paperclip, StickyNote, Users,
} from 'lucide-react';
import { erpAPI } from '../../services/api';
import Toast from '../../components/admin/Toast';
import ReturnExchangeWorkspace from './ReturnExchangeWorkspace';

const money = (v: any) => `\u09F3${Number(v || 0).toLocaleString('en-BD', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const date = (v: any) => v ? new Date(v).toLocaleDateString('en-BD') : '-';
const dt = (v: any) => v ? new Date(v).toLocaleString('en-BD') : '-';
const rows = (p: any): any[] => Array.isArray(p) ? p : (p?.results || []);
const errorText = (e: any) => e?.response?.data?.detail || e?.message || 'Request failed';
const variantLabel = (v: any) => [v.product_name || v.product?.name_en, v.color, v.size].filter(Boolean).join(' - ') || v.sku || 'Item';

type DrawerTab = 'summary' | 'items' | 'pricing' | 'payments' | 'timeline' | 'accounting' | 'documents' | 'returns';

export default function SalesList({ me, locationFilter }: { me?: any; locationFilter?: string }) {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<any>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState<DrawerTab>('summary');
  const [locations, setLocations] = useState<any[]>([]);

  // Return/Exchange workspace
  const [exchangeSale, setExchangeSale] = useState<any>(null);
  const [exchangeData, setExchangeData] = useState<any>(null);

  // Void / refund
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);

  // Customer history
  const [customerHistory, setCustomerHistory] = useState<any>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Notes / attachments
  const [newNote, setNewNote] = useState('');
  const [noteVisibility, setNoteVisibility] = useState<'CUSTOMER' | 'INTERNAL'>('INTERNAL');
  const [newAttachment, setNewAttachment] = useState<any>(null);
  const [attachmentDesc, setAttachmentDesc] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  // permissions map
  const perms = useMemo(() => new Set(me?.permissions || []), [me]);
  const can = useCallback((code: string) => perms.has(code), [perms]);

  const printInvoice = (sale: any) => {
    if (!sale) return;
    const w = window.open('', '_blank', 'width=800,height=600');
    if (w) {
      w.document.write(`<html><head><title>Invoice ${sale.sale_number}</title>
        <style>body{font-family:sans-serif;padding:30px;max-width:400px;margin:auto}
        h1{font-size:16px;margin-bottom:4px;color:#1e293b}
        .meta{font-size:12px;color:#64748b;margin-bottom:16px}
        table{width:100%;border-collapse:collapse;font-size:13px}
        th,td{padding:6px 4px;text-align:left;border-bottom:1px solid #e2e8f0}
        .right{text-align:right}
        .total{font-weight:700;font-size:15px;margin-top:12px;text-align:right}
        .footer{font-size:11px;color:#94a3b8;text-align:center;margin-top:24px;border-top:1px dashed #e2e8f0;padding-top:12px}
        .qrcode{width:90px;height:90px;margin:12px auto;background:repeating-linear-gradient(45deg,#333 0 4px,#fff 4px 8px);border:1px solid #e2e8f0}
        .printtime{font-size:10px;color:#94a3b8;text-align:center;margin-top:6px}</style></head><body>
        <h1>POS Invoice</h1>
        <div class="meta">${sale.sale_number || ''}<br>${dt(new Date())}<br>${sale.shop_name || ''}</div>
        <div class="meta">Customer: ${sale.customer_name || 'Walk-in'}${sale.customer_phone ? ' · ' + sale.customer_phone : ''}<br>Cashier: ${sale.cashier_name || '-'}</div>
        <table><tr><th>Item</th><th class="right">Qty</th><th class="right">Price</th><th class="right">Total</th></tr>
        ${(sale.items || []).map((i: any) => '<tr><td>' + variantLabel(i) + '</td><td class="right">' + i.quantity + '</td><td class="right">' + money(i.unit_price) + '</td><td class="right">' + money(i.unit_price * i.quantity) + '</td></tr>').join('')}
        </table>
        <div style="font-size:13px;margin-top:8px;text-align:right">
          <div>Subtotal: ${money(sale.subtotal || 0)}</div>
          ${(sale.discount_amount - (sale.coupon_discount || 0) > 0) ? `<div style="color:#dc2626">Auto Discounts: -${money((sale.discount_amount || 0) - (sale.coupon_discount || 0))}</div>` : ''}
          ${Number(sale.coupon_discount) > 0 ? `<div style="color:#dc2626">Coupon Discount: -${money(sale.coupon_discount)}</div>` : ''}
          ${sale.discount_amount > 0 ? `<div style="border-top:1px solid #e2e8f0;padding-top:4px;margin-top:4px">Total Discount: -${money(sale.discount_amount)}</div>` : ''}
        </div>
        <div class="total">Grand Total: ${money(sale.total_amount)}</div>
        <div class="qrcode"></div>
        <div class="printtime">Printed: ${dt(new Date())}</div>
        <div class="footer">Thank you for your purchase!</div></body></html>`);
      w.document.close();
      w.print();
    }
    // Track print server-side
    try { erpAPI.action('sales', sale.id, sale.print_count ? 'reprint' : 'print_invoice', {}); } catch {}
  };

  const showLoc = useMemo(() => !locationFilter, [locationFilter]);
  const params = useMemo(() => {
    const p: Record<string, any> = { page_size: 200 };
    if (locationFilter) p.location = locationFilter;
    return p;
  }, [locationFilter]);

  const loadData = useCallback(() => {
    setLoading(true);
    const pm = { ...params };
    if (search) pm.search = search;
    Promise.all([
      erpAPI.list('sales', pm).then(r => setData(rows(r.data))),
      erpAPI.list('locations', {}).then(r => setLocations(rows(r.data))),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, [params, search]);

  useEffect(() => { loadData(); }, [loadData]);

  const kpis = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const todaySales = data.filter(r => r.completed_at && r.completed_at.slice(0, 10) === today);
    const revenue = todaySales.reduce((s, r) => s + Number(r.total_amount || 0), 0);
    const txCount = todaySales.length;
    const refunds = data.filter(r => r.status === 'RETURNED' || r.status === 'PARTIALLY_RETURNED').length;
    return { revenue, txCount, avgSale: txCount ? revenue / txCount : 0, refunds };
  }, [data]);

  const loadDetail = useCallback(async (item: any) => {
    try {
      const [detailResp, jeSale, jeReversal] = await Promise.all([
        erpAPI.get('sales', item.id),
        erpAPI.list('journal-entries', { reference_type: 'Sale', reference_id: String(item.id), page_size: 10 }),
        erpAPI.list('journal-entries', { reference_type: 'SaleReversal', reference_id: String(item.id), page_size: 10 }),
      ]);
      const journalEntries = [...(jeSale.data?.results || []), ...(jeReversal.data?.results || [])];
      return { ...detailResp.data, journalEntries };
    } catch { return item; }
  }, []);

  const openDrawer = async (item: any) => {
    const detail = await loadDetail(item);
    setSelected(detail);
    setDrawerTab('summary');
    setDrawerOpen(true);
    setCustomerHistory(null);
    if (detail.customer_phone) {
      loadCustomerHistory(detail.customer_phone);
    }
  };

  const loadCustomerHistory = async (phone: string) => {
    setLoadingHistory(true);
    try {
      const res = await erpAPI.list('sales/customer_history', { phone });
      setCustomerHistory(res.data);
    } catch { setCustomerHistory(null); }
    finally { setLoadingHistory(false); }
  };

  const onClickCustomerHistory = (sale: any) => {
    openDrawerFromId(sale.id);
  };

  const openDrawerFromId = async (id: number) => {
    const item = data.find(d => d.id === id) || { id };
    const detail = await loadDetail(item);
    setSelected(detail);
    setDrawerTab('summary');
    setDrawerOpen(true);
    if (detail.customer_phone) loadCustomerHistory(detail.customer_phone);
  };

  const performVoid = async () => {
    if (!selected || !voidReason.trim()) return;
    setVoiding(true);
    try {
      await erpAPI.action('sales', selected.id, 'void', { reason: voidReason.trim() });
      const d = await loadDetail(selected);
      setSelected(d);
      setVoidOpen(false);
      setVoidReason('');
      loadData();
      setMessage({ type: 'success', text: `Sale ${d.sale_number} voided and fully reversed.` });
    } catch (e) { setMessage({ type: 'error', text: errorText(e) }); }
    finally { setVoiding(false); }
  };

  const addSaleNote = async () => {
    if (!newNote.trim() || !selected) return;
    try {
      await erpAPI.action('sales', selected.id, 'add_note', { note: newNote.trim(), visibility: noteVisibility });
      setNewNote('');
      const d = await loadDetail(selected);
      setSelected(d);
      setMessage({ type: 'success', text: 'Note added.' });
    } catch (e) { setMessage({ type: 'error', text: errorText(e) }); }
  };

  const uploadAttachment = async () => {
    if (!newAttachment || !selected) return;
    const fd = new FormData();
    fd.append('file', newAttachment);
    fd.append('description', attachmentDesc);
    try {
      await erpAPI.action('sales', selected.id, 'add_attachment', fd);
      setNewAttachment(null); setAttachmentDesc('');
      if (fileRef.current) fileRef.current.value = '';
      const d = await loadDetail(selected);
      setSelected(d);
      setDrawerTab('documents');
      setMessage({ type: 'success', text: 'Attachment uploaded.' });
    } catch (e) { setMessage({ type: 'error', text: errorText(e) }); }
  };

  const startExchange = async (sale: any) => {
    try {
      const [detail, extraData] = await Promise.all([
        loadDetail(sale),
        Promise.all([
          erpAPI.list('variants', { page_size: 200 }),
          erpAPI.list('inventory', locationFilter ? { page_size: 500, location: Number(locationFilter) } : { page_size: 500 }),
          erpAPI.list('shop-prices', locationFilter ? { page_size: 500, shop: Number(locationFilter) } : { page_size: 500 }),
          erpAPI.list('payment-accounts', locationFilter ? { location: Number(locationFilter) } : {}),
        ]),
      ]);
      setExchangeSale(detail);
      setExchangeData({
        products: rows(extraData[0].data),
        inventory: rows(extraData[1].data),
        'shop-prices': rows(extraData[2].data),
        'payment-accounts': rows(extraData[3].data),
      });
      setDrawerOpen(false);
    } catch (e) {
      setMessage({ type: 'error', text: 'Could not load sale details.' });
    }
  };

  const exportCSV = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'sales.json'; a.click();
    URL.revokeObjectURL(url);
  };

  const locName = (id: number) => locations.find((l: any) => l.id === id)?.name || '-';

  const TABS: DrawerTab[] = ['summary', 'items', 'pricing', 'payments', 'timeline', 'accounting', 'documents', 'returns'];

  return <div className="erp-page">
    <Toast show={Boolean(message)} message={message?.text || ''} type={message?.type || 'info'} onClose={() => setMessage(null)} />

    {exchangeSale ? (
      <ReturnExchangeWorkspace
        sale={exchangeSale}
        onClose={() => setExchangeSale(null)}
        onComplete={() => { setExchangeSale(null); loadData(); }}
        paymentMethods={exchangeData?.['payment-accounts'] || []}
        variants={exchangeData?.products || []}
        inventory={exchangeData?.inventory || []}
        shopPrices={exchangeData?.['shop-prices'] || []}
      />
    ) : (<>
      {/* KPI Cards */}
      <div className="acct-kpi-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="erp-stat"><span>Today's Revenue</span><strong style={{ color: '#059669' }}>{money(kpis.revenue)}</strong></div>
        <div className="erp-stat"><span>Transactions</span><strong style={{ color: '#2563eb' }}>{kpis.txCount}</strong></div>
        <div className="erp-stat"><span>Avg Sale</span><strong style={{ color: '#7c3aed' }}>{money(kpis.avgSale)}</strong></div>
        <div className="erp-stat"><span>Refunds</span><strong style={{ color: '#dc2626' }}>{kpis.refunds}</strong></div>
      </div>

      {/* Advanced Search */}
      <div className="erp-filterbar" style={{ flexWrap: 'wrap' }}>
        <Search size={15} style={{ color: '#94a3b8' }} />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Invoice, customer, phone, SKU, product, coupon, payment ref, cashier..." style={{ border: 0, background: 'transparent', flex: 1, minWidth: 200, outline: 'none' }} />
        <button onClick={exportCSV} className="erp-btn secondary" style={{ padding: '5px 8px', fontSize: '.7rem', marginLeft: 'auto' }}><Download size={12} /> Export</button>
      </div>

      {/* Table */}
      {loading ? <div className="erp-loading"><RefreshCw className="spin" /> Loading...</div> :
        <section className="erp-card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="erp-table-wrap" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
            <table style={{ position: 'relative' }}>
              <thead><tr style={{ position: 'sticky', top: 0, zIndex: 2, background: '#fff' }}>
                <th>Invoice</th>{showLoc && <th>Shop</th>}<th>Customer</th><th>Phone</th><th>Subtotal</th><th>Discount</th><th>Total</th><th>Status</th><th>Payment</th><th>Date</th><th style={{ width: 160 }}>Actions</th>
              </tr></thead>
              <tbody>{data.length === 0 ? <tr><td className="erp-empty" colSpan={showLoc ? 11 : 10}>No sales found.</td></tr> :
                data.map((r: any) => <tr key={r.id}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <td onClick={() => openDrawer(r)} style={{ fontWeight: 600, fontSize: '.78rem', cursor: 'pointer' }}>{r.sale_number}</td>
                  {showLoc && <td onClick={() => openDrawer(r)} style={{ fontSize: '.75rem', color: '#4f46e5', fontWeight: 600, cursor: 'pointer' }}>{r.shop_name || locName(r.shop)}</td>}
                  <td onClick={() => openDrawer(r)} style={{ cursor: 'pointer' }}>{r.customer_name || 'Walk-in'}</td>
                  <td onClick={() => openDrawer(r)} style={{ fontSize: '.78rem', color: '#64748b', cursor: 'pointer' }}>{r.customer_phone || '-'}</td>
                  <td onClick={() => openDrawer(r)} style={{ fontWeight: 500, cursor: 'pointer' }}>{money(r.subtotal)}</td>
                  <td onClick={() => openDrawer(r)} style={{ color: '#dc2626', cursor: 'pointer' }}>{money(r.discount_amount)}</td>
                  <td onClick={() => openDrawer(r)} style={{ fontWeight: 700, cursor: 'pointer' }}>{money(r.total_amount)}</td>
                  <td><span className={`erp-status ${r.status === 'COMPLETED' ? 'received' : r.status === 'RETURNED' || r.status === 'VOID' ? 'cancelled' : ''}`}>{r.status === 'PARTIALLY_RETURNED' ? 'Part Returned' : r.status}</span></td>
                  <td onClick={() => openDrawer(r)} style={{ cursor: 'pointer' }}><span style={{ padding: '2px 6px', borderRadius: 4, fontSize: '.65rem', fontWeight: 600, background: r.payment_status === 'PAID' ? '#d1fae5' : r.payment_status === 'REFUNDED' ? '#fee2e2' : '#fef3c7', color: r.payment_status === 'PAID' ? '#065f46' : r.payment_status === 'REFUNDED' ? '#991b1b' : '#92400e' }}>{r.payment_status || '-'}</span></td>
                  <td onClick={() => openDrawer(r)} style={{ fontSize: '.72rem', cursor: 'pointer' }}>{r.completed_at ? date(r.completed_at) : '-'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, whiteSpace: 'nowrap' }}>
                      <button onClick={() => openDrawer(r)} className="erp-btn secondary" style={{ padding: '3px 6px', fontSize: '.6rem' }} title="View Details"><FileText size={11} /></button>
                      {r.status === 'COMPLETED' && (
                        <button onClick={() => startExchange(r)} className="erp-btn secondary" style={{ padding: '3px 6px', fontSize: '.6rem', background: '#eef2ff', color: '#4f46e5' }} title="Return / Exchange">
                          <RotateCcw size={11} />
                        </button>
                      )}
                      {r.status === 'COMPLETED' && (
                        <button onClick={() => printInvoice(r)} className="erp-btn secondary" style={{ padding: '3px 6px', fontSize: '.6rem' }} title="Print / Reprint Invoice">
                          <Printer size={11} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>)}</tbody>
            </table>
          </div>
        </section>}

      {/* ─── Professional Sale Detail Drawer ─── */}
      {drawerOpen && selected && <div className="acct-modal-overlay" onClick={() => setDrawerOpen(false)}>
        <div className="acct-modal" style={{ maxWidth: 680 }} onClick={e => e.stopPropagation()}>
          <div className="acct-modal-header">
            <div>
              <h3 className="acct-modal-title" style={{ margin: 0 }}>
                {selected.sale_number || 'Sale'}
                <span style={{ marginLeft: 8, verticalAlign: 'middle' }} className={`erp-status ${selected.status === 'COMPLETED' ? 'received' : selected.status === 'VOID' || selected.status === 'RETURNED' ? 'cancelled' : ''}`}>{selected.status}</span>
              </h3>
              <span style={{ fontSize: '.75rem', color: '#64748b' }}>{selected.customer_name || 'Walk-in'} · {selected.customer_phone || '-'}</span>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => printInvoice(selected)} className="acct-btn" style={{ padding: '6px 10px', fontSize: '.72rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                <Printer size={13} /> {selected.print_count ? `Reprint (${selected.print_count})` : 'Print'}
              </button>
              {selected.status === 'COMPLETED' && (
                <button onClick={() => { setDrawerOpen(false); startExchange(selected); }}
                  className="acct-btn" style={{ padding: '6px 10px', fontSize: '.72rem', background: '#eef2ff', color: '#4f46e5', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <RotateCcw size={13} /> Return / Exchange
                </button>
              )}
              <button className="acct-modal-close" onClick={() => setDrawerOpen(false)}><X size={16} /></button>
            </div>
          </div>

          <div className="acct-tabs" style={{ padding: '0 16px', marginBottom: 0, overflowX: 'auto' }}>
            {TABS.map(tab => (
              <button key={tab} className={`acct-tab ${drawerTab === tab ? 'active' : ''}`} onClick={() => setDrawerTab(tab)}
                style={{ textTransform: 'capitalize' }}>{tab}</button>
            ))}
          </div>

          <div className="acct-modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {drawerTab === 'summary' && <>
              {customerHistory && <div style={{ padding: '8px 12px', background: '#eef2ff', borderRadius: 8, marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
                <strong style={{ fontSize: '.78rem' }}><Users size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />Repeat Customer</strong>
                <span style={{ fontSize: '.72rem' }}>Orders: <strong>{customerHistory.customer?.total_orders || 0}</strong></span>
                <span style={{ fontSize: '.72rem' }}>Lifetime: <strong>{money(customerHistory.customer?.total_spending)}</strong></span>
                <span style={{ fontSize: '.72rem' }}>Avg: <strong>{money(customerHistory.customer?.avg_order_value)}</strong></span>
                <span style={{ fontSize: '.72rem' }}>Last: <strong>{customerHistory.customer?.last_purchase ? date(customerHistory.customer.last_purchase) : '-'}</strong></span>
                <span style={{ fontSize: '.72rem' }}>Coupons: <strong>{customerHistory.customer?.coupons_used?.length || 0}</strong></span>
                <button onClick={() => setDrawerTab('payments')} style={{ marginLeft: 'auto', border: '1px solid #c7d2fe', background: '#fff', color: '#4f46e5', borderRadius: 6, padding: '3px 8px', fontSize: '.65rem', fontWeight: 600, cursor: 'pointer' }}>View History</button>
              </div>}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                <div><strong style={{ color: '#64748b', fontSize: '.68rem', textTransform: 'uppercase' }}>Subtotal</strong><div style={{ fontWeight: 700 }}>{money(selected.subtotal)}</div></div>
                <div><strong style={{ color: '#64748b', fontSize: '.68rem', textTransform: 'uppercase' }}>Auto Discount</strong><div style={{ fontWeight: 700, color: '#dc2626' }}>{money((selected.discount_amount || 0) - (selected.coupon_discount || 0))}</div></div>
                {Number(selected.coupon_discount) > 0 && <div><strong style={{ color: '#64748b', fontSize: '.68rem', textTransform: 'uppercase' }}>Coupon Discount</strong><div style={{ fontWeight: 700, color: '#dc2626' }}>{money(selected.coupon_discount)}</div></div>}
                <div><strong style={{ color: '#64748b', fontSize: '.68rem', textTransform: 'uppercase' }}>Total Discount</strong><div style={{ fontWeight: 700, color: '#dc2626' }}>{money(selected.discount_amount)}</div></div>
                <div><strong style={{ color: '#64748b', fontSize: '.68rem', textTransform: 'uppercase' }}>Grand Total</strong><div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#059669' }}>{money(selected.total_amount)}</div></div>
                <div><strong style={{ color: '#64748b', fontSize: '.68rem', textTransform: 'uppercase' }}>Shop</strong><div>{selected.shop_name || locName(selected.shop)}</div></div>
                <div><strong style={{ color: '#64748b', fontSize: '.68rem', textTransform: 'uppercase' }}>Cashier</strong><div>{selected.cashier_name || '-'}</div></div>
                <div><strong style={{ color: '#64748b', fontSize: '.68rem', textTransform: 'uppercase' }}>Completed</strong><div>{selected.completed_at ? dt(selected.completed_at) : '-'}</div></div>
                <div><strong style={{ color: '#64748b', fontSize: '.68rem', textTransform: 'uppercase' }}>Payment Status</strong><div><span style={{ padding: '2px 6px', borderRadius: 4, fontSize: '.7rem', fontWeight: 600, background: selected.payment_status === 'PAID' ? '#d1fae5' : selected.payment_status === 'REFUNDED' ? '#fee2e2' : '#fef3c7', color: selected.payment_status === 'PAID' ? '#065f46' : selected.payment_status === 'REFUNDED' ? '#991b1b' : '#92400e' }}>{selected.payment_status || '-'}</span></div></div>
                <div><strong style={{ color: '#64748b', fontSize: '.68rem', textTransform: 'uppercase' }}>Accounting Status</strong><div><span style={{ padding: '2px 6px', borderRadius: 4, fontSize: '.7rem', fontWeight: 600, background: selected.accounting_status === 'POSTED' ? '#d1fae5' : '#fef3c7', color: selected.accounting_status === 'POSTED' ? '#065f46' : '#92400e' }}>{selected.accounting_status || '-'}</span></div></div>
                {selected.coupon_code && <div><strong style={{ color: '#64748b', fontSize: '.68rem', textTransform: 'uppercase' }}>Coupon</strong><div><span style={{ padding: '2px 6px', borderRadius: 4, background: '#f0fdf4', color: '#059669', fontSize: '.7rem', fontWeight: 600 }}>{selected.coupon_code}</span><span style={{ marginLeft: 4, fontSize: '.68rem', color: '#64748b' }}>{selected.coupon_discount_value !== undefined ? `(${selected.coupon_discount_value}${selected.coupon_discount_value > 100 ? '' : '%'})` : ''}</span></div></div>}
                {selected.print_count > 0 && <div><strong style={{ color: '#64748b', fontSize: '.68rem', textTransform: 'uppercase' }}>Printed</strong><div style={{ fontSize: '.75rem' }}>{selected.print_count}× {selected.last_printed_at ? '· last ' + dt(selected.last_printed_at) : ''}</div></div>}
                {selected.status === 'VOID' && <div><strong style={{ color: '#64748b', fontSize: '.68rem', textTransform: 'uppercase' }}>Void Reason</strong><div style={{ fontSize: '.78rem', color: '#dc2626' }}>{selected.void_reason || '-'}</div></div>}
              </div>
            </>}

            {drawerTab === 'items' && <ItemsTab items={selected.items} />}

            {drawerTab === 'pricing' && <PricingTab items={selected.items} />}

            {drawerTab === 'payments' && <>
              <h4 style={{ margin: '0 0 8px', fontSize: '.85rem' }}>Payments</h4>
              {(!selected.payments || selected.payments.length === 0) ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>No payments recorded.</div>
              ) : (
                <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.78rem' }}>
                    <thead><tr style={{ background: '#f8fafc' }}>
                      <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '.62rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b' }}>Method</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: '.62rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b' }}>Amount</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '.62rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b' }}>Reference</th>
                    </tr></thead>
                    <tbody>{selected.payments.map((p: any, i: number) => <tr key={i}>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9' }}><span style={{ padding: '2px 6px', borderRadius: 4, background: '#eff6ff', color: '#1d4ed8', fontSize: '.7rem', fontWeight: 600 }}>{p.method_name || p.method || '-'}</span></td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', fontWeight: 600 }}>{money(p.amount)}</td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9', fontSize: '.75rem', color: '#64748b' }}>{p.reference || '-'}</td>
                    </tr>)}</tbody>
                  </table>
                </div>
              )}
              {selected.refunds?.length > 0 && <>
                <h4 style={{ margin: '12px 0 8px', fontSize: '.85rem' }}>Refunds</h4>
                <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.78rem' }}>
                    <thead><tr style={{ background: '#f8fafc' }}>
                      <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '.62rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b' }}>Account</th>
                      <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: '.62rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b' }}>Amount</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '.62rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b' }}>Reason</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '.62rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b' }}>By</th>
                    </tr></thead>
                    <tbody>{selected.refunds.map((rf: any, i: number) => <tr key={i}>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9' }}>{rf.refund_account_name || '-'}</td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', color: '#dc2626', fontWeight: 600 }}>{money(rf.amount)}</td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9', fontSize: '.72rem', color: '#64748b' }}>{rf.reason || '-'}</td>
                      <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9', fontSize: '.72rem' }}>{rf.processed_by_name || '-'} · {dt(rf.processed_at)}</td>
                    </tr>)}</tbody>
                  </table>
                </div>
              </>}
            </>}

            {drawerTab === 'timeline' && <>
              <h4 style={{ margin: '0 0 8px', fontSize: '.85rem' }}>Sale Timeline</h4>
              {(!selected.timeline || selected.timeline.length === 0) ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>No timeline entries.</div>
              ) : (
                <div style={{ position: 'relative', paddingLeft: 20 }}>
                  <div style={{ position: 'absolute', left: 8, top: 0, bottom: 0, width: 2, background: '#e2e8f0' }} />
                  {selected.timeline.map((entry: any, i: number) => <div key={i} style={{ position: 'relative', padding: '0 0 14px 16px' }}>
                    <div style={{ position: 'absolute', left: -16, top: 4, width: 10, height: 10, borderRadius: '50%', background: '#4f46e5', border: '2px solid #eef2ff' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '.78rem' }}>{entry.action}</div>
                        {entry.notes && <div style={{ fontSize: '.7rem', color: '#64748b', marginTop: 2 }}>{entry.notes}</div>}
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontSize: '.65rem', color: '#94a3b8' }}>{dt(entry.timestamp)}</div>
                        <div style={{ fontSize: '.65rem', color: '#64748b' }}>{entry.user_name || '-'}</div>
                      </div>
                    </div>
                  </div>)}
                </div>
              )}
            </>}

            {drawerTab === 'accounting' && <>
              <h4 style={{ margin: '0 0 8px', fontSize: '.85rem' }}>Journal References</h4>
              {(!selected.journalEntries || selected.journalEntries.length === 0) ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>No journal entries.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {selected.journalEntries.map((je: any, i: number) => <div key={i} style={{ padding: '10px', background: '#f8fafc', borderRadius: 8 }}>
                    <div style={{ fontWeight: 600, fontSize: '.78rem', fontFamily: 'monospace' }}>{je.entry_number}</div>
                    <div style={{ fontSize: '.72rem', color: '#64748b', marginTop: 2 }}>{je.description}</div>
                    <div style={{ display: 'flex', gap: 16, marginTop: 4, fontSize: '.75rem' }}>
                      <span>Debit: <strong style={{ color: '#dc2626' }}>{money(je.total_debit || 0)}</strong></span>
                      <span>Credit: <strong style={{ color: '#059669' }}>{money(je.total_credit || 0)}</strong></span>
                    </div>
                  </div>)}
                </div>
              )}
            </>}

            {drawerTab === 'documents' && <DocumentsTab selected={selected} newNote={newNote} setNewNote={setNewNote}
              noteVisibility={noteVisibility} setNoteVisibility={setNoteVisibility} addSaleNote={addSaleNote}
              newAttachment={newAttachment} setNewAttachment={setNewAttachment} attachmentDesc={attachmentDesc}
              setAttachmentDesc={setAttachmentDesc} uploadAttachment={uploadAttachment} fileRef={fileRef} />}

            {drawerTab === 'returns' && <>
              <h4 style={{ margin: '0 0 8px', fontSize: '.85rem' }}>Returns & Exchanges</h4>
              <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>
                {selected.status === 'COMPLETED' ? (
                  <div>
                    <RotateCcw size={28} style={{ opacity: .2, marginBottom: 8 }} />
                    <p style={{ margin: '0 0 12px' }}>Process a return or exchange for this sale.</p>
                    {can('pos.exchange.create') && (
                      <button onClick={() => { setDrawerOpen(false); startExchange(selected); }} className="acct-btn" style={{ padding: '8px 14px', background: '#4f46e5', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <RotateCcw size={14} /> Start Return / Exchange
                      </button>
                    )}
                    {can('pos.sale.void') && (
                      <button onClick={() => { setVoidReason(''); setVoidOpen(true); }}
                        style={{ marginLeft: 8, padding: '8px 14px', background: '#dc2626', color: '#fff', border: 0, borderRadius: 7, display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontWeight: 600 }}>
                        <X size={14} /> Void / Refund Sale
                      </button>
                    )}
                    <p style={{ margin: '12px 0 0', fontSize: '.72rem', color: '#94a3b8' }}>
                      Voiding restores inventory and fully reverses revenue, COGS and the payment(s) with an audit reversal journal.
                    </p>
                  </div>
                ) : (
                  <div>This sale has already been returned ({selected.status}).</div>
                )}
              </div>
            </>}
          </div>
        </div>
      </div>}

      {voidOpen && selected && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 20, maxWidth: 460, width: '100%' }}>
            <h3 style={{ margin: '0 0 6px', fontSize: '1rem', color: '#b91c1c' }}>Void / Refund Sale</h3>
            <p style={{ margin: '0 0 14px', fontSize: '.8rem', color: '#64748b' }}>
              <strong>{selected.sale_number}</strong> · {money(selected.total_amount)} · {selected.shop_name || '-'}
            </p>
            <div style={{ padding: '10px 12px', borderRadius: 8, background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: '.78rem', marginBottom: 14, fontWeight: 600 }}>
              This will restore inventory and reverse revenue, COGS and the payment account(s) ({selected.payments?.length || 0} payment(s)). This cannot be undone.
            </div>
            <label style={{ display: 'block', fontSize: '.72rem', fontWeight: 700, color: '#475569', marginBottom: 6 }}>Reason / Reference (required)</label>
            <textarea value={voidReason} onChange={e => setVoidReason(e.target.value)} rows={3}
              placeholder="Required — why is this sale being voided?" autoFocus
              style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #cbd5e1', borderRadius: 8, padding: 8, fontSize: '.8rem', marginBottom: 14 }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setVoidOpen(false)} disabled={voiding} style={{ padding: '8px 14px', borderRadius: 7, border: '1px solid #cbd5e1', background: '#fff', color: '#475569', fontWeight: 600, cursor: 'pointer', fontSize: '.8rem' }}>Cancel</button>
              <button onClick={performVoid} disabled={voiding || !voidReason.trim()}
                style={{ padding: '8px 14px', borderRadius: 7, border: 0, background: '#dc2626', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: '.8rem', opacity: voiding || !voidReason.trim() ? .6 : 1, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {voiding ? <><RotateCcw className="spin" size={14} /> Voiding…</> : <>Confirm Void</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </>)}
  </div>;
}

/* ─── Sub-components ─── */

function ItemsTab({ items }: { items: any[] }) {
  return <>
    <h4 style={{ margin: '0 0 8px', fontSize: '.85rem' }}>Items</h4>
    {(!items || items.length === 0) ? (
      <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>No items loaded.</div>
    ) : (
      <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e2e8f0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.78rem' }}>
          <thead><tr style={{ background: '#f8fafc' }}>
            <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '.62rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b' }}>Item</th>
            <th style={{ padding: '6px 8px', textAlign: 'center', fontSize: '.62rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b' }}>Qty</th>
            <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: '.62rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b' }}>Unit Price</th>
            <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: '.62rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b' }}>Total</th>
          </tr></thead>
          <tbody>{items.map((item: any, i: number) => <tr key={i}>
            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9', fontWeight: 500 }}>{variantLabel(item)}</td>
            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9', textAlign: 'center' }}>{item.quantity}</td>
            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9', textAlign: 'right' }}>{money(item.unit_price)}</td>
            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', fontWeight: 600 }}>{money(item.unit_price * item.quantity)}</td>
          </tr>)}</tbody>
        </table>
      </div>
    )}
  </>;
}

function PricingTab({ items }: { items: any[] }) {
  return <>
    <h4 style={{ margin: '0 0 8px', fontSize: '.85rem' }}>Pricing Snapshot</h4>
    {(!items || items.length === 0) ? (
      <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>No pricing data.</div>
    ) : (
      <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e2e8f0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.75rem' }}>
          <thead><tr style={{ background: '#f8fafc' }}>
            <th style={{ padding: '6px 8px', textAlign: 'left', fontSize: '.6rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b' }}>Item</th>
            <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: '.6rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b' }}>Orig</th>
            <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: '.6rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b' }}>Scheduled</th>
            <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: '.6rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b' }}>Rule</th>
            <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: '.6rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b' }}>Coupon</th>
            <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: '.6rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b' }}>Final</th>
            <th style={{ padding: '6px 8px', textAlign: 'right', fontSize: '.6rem', fontWeight: 700, textTransform: 'uppercase', color: '#64748b' }}>Line Tot</th>
          </tr></thead>
          <tbody>{items.map((item: any, i: number) => {
            const scheduled = Number(item.scheduled_discount || 0) * item.quantity;
            const rule = Number(item.rule_discount || 0) * item.quantity;
            const coupon = Number(item.coupon_discount_allocated || 0);
            return <tr key={i}>
              <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9', fontWeight: 500 }}>{variantLabel(item)}</td>
              <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9', textAlign: 'right' }}>{money(item.original_price || item.unit_price)}</td>
              <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', color: scheduled > 0 ? '#dc2626' : '#94a3b8' }}>{scheduled > 0 ? '-' + money(scheduled) : '—'}</td>
              <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', color: rule > 0 ? '#dc2626' : '#94a3b8' }}>{rule > 0 ? '-' + money(rule) : '—'}</td>
              <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', color: coupon > 0 ? '#dc2626' : '#94a3b8' }}>{coupon > 0 ? '-' + money(coupon) : '—'}</td>
              <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', fontWeight: 600 }}>{money(item.final_unit_price || item.unit_price)}</td>
              <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', fontWeight: 700 }}>{money(item.line_total || item.unit_price * item.quantity)}</td>
            </tr>;
          })}</tbody>
        </table>
      </div>
    )}
  </>;
}

function DocumentsTab(props: any) {
  const { selected, newNote, setNewNote, noteVisibility, setNoteVisibility, addSaleNote, newAttachment, setNewAttachment, attachmentDesc, setAttachmentDesc, uploadAttachment, fileRef } = props;
  return <>
    <h4 style={{ margin: '0 0 8px', fontSize: '.85rem' }}><StickyNote size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />Notes</h4>
    <div style={{ marginBottom: 10 }}>
      <textarea value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Add a note..."
        style={{ width: '100%', minHeight: 60, border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px', fontSize: '.78rem' }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
        <select value={noteVisibility} onChange={e => setNoteVisibility(e.target.value as any)}
          style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '5px 8px', fontSize: '.72rem' }}>
          <option value="INTERNAL">Internal (staff only)</option>
          <option value="CUSTOMER">Customer-visible</option>
        </select>
        <button onClick={addSaleNote} className="acct-btn" style={{ padding: '6px 12px', fontSize: '.72rem' }}>Add Note</button>
      </div>
    </div>
    {(selected.notes || []).length > 0 && (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
        {selected.notes.map((n: any, i: number) => (
          <div key={i} style={{ padding: '8px 10px', borderRadius: 8, background: n.visibility === 'INTERNAL' ? '#fffbeb' : '#f0fdf4', border: '1px solid #fef3c7', fontSize: '.75rem' }}>
            <div>{n.created_by_name || 'Staff'} · {dt(n.created_at)}</div>
            <div style={{ marginTop: 2, color: '#334155' }}>{n.note}</div>
          </div>
        ))}
      </div>
    )}

    <h4 style={{ margin: '16px 0 8px', fontSize: '.85rem' }}><Paperclip size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Attachments</h4>
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
      <input ref={fileRef} type="file" onChange={e => setNewAttachment(e.target.files?.[0] || null)}
        style={{ fontSize: '.72rem' }} />
      <input value={attachmentDesc} onChange={e => setAttachmentDesc(e.target.value)} placeholder="Description" style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 8px', fontSize: '.72rem' }} />
      <button onClick={uploadAttachment} disabled={!newAttachment} className="acct-btn" style={{ padding: '6px 12px', fontSize: '.72rem' }}>Upload</button>
    </div>
    {(selected.attachments || []).length === 0 ? (
      <div style={{ padding: 14, textAlign: 'center', color: '#94a3b8', fontSize: '.75rem' }}>No attachments.</div>
    ) : (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {selected.attachments.map((a: any, i: number) => (
          <div key={i} style={{ padding: '8px 10px', borderRadius: 8, background: '#f8fafc', border: '1px solid #e2e8f0', fontSize: '.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 600 }}>{a.filename}</div>
              {a.description && <div style={{ color: '#64748b', fontSize: '.7rem' }}>{a.description}</div>}
              <div style={{ color: '#94a3b8', fontSize: '.68rem' }}>{a.uploaded_by_name || '-'} · {dt(a.uploaded_at)}</div>
            </div>
          </div>
        ))}
      </div>
    )}
  </>;
}

